import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaClock, FaDownload, FaExclamationTriangle, FaImages } from 'react-icons/fa';
import { downloadPhoto, getCinetPayPaymentStatus, getImageUrl, getMyPurchases } from '../services/api';
import { useCart } from '../context/CartContext';
import './Checkout.css';

const normalizeStatus = (status) => String(status || 'pending').toLowerCase();

const getPhotoDisplayName = (purchase) => {
  const photo = purchase?.photo || {};
  const name = photo.name || photo.title || photo.filename || `Photo ${purchase?.photo_id || ''}`;
  return String(name).split('/').pop() || name;
};

function CheckoutResult() {
  const [searchParams] = useSearchParams();
  const transaction = searchParams.get('transaction') || '';
  const payment = searchParams.get('payment') || '';
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(payment || 'pending');
  const [purchases, setPurchases] = useState([]);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    const loadResult = async () => {
      setLoading(true);
      setError('');

      try {
        if (transaction) {
          const response = await getCinetPayPaymentStatus(transaction);
          const nextPurchases = Array.isArray(response.purchases) ? response.purchases : [];
          setStatus(response.payment_status || payment || 'pending');
          setPurchases(nextPurchases);
          if (nextPurchases.some(item => normalizeStatus(item.payment_status) === 'completed')) {
            clearCart();
          }
        } else {
          const response = await getMyPurchases();
          const completed = (response.purchases || []).filter(item => normalizeStatus(item.payment_status) === 'completed');
          setStatus(completed.length > 0 ? 'success' : payment || 'pending');
          setPurchases(completed);
        }
      } catch (err) {
        setError(err.message || 'Impossible de verifier le paiement.');
      } finally {
        setLoading(false);
      }
    };

    loadResult();
  }, [clearCart, payment, transaction]);

  const completedPurchases = useMemo(
    () => purchases.filter(item => normalizeStatus(item.payment_status) === 'completed'),
    [purchases]
  );
  const displayPurchases = completedPurchases.length > 0 ? completedPurchases : purchases;
  const normalizedStatus = normalizeStatus(status);
  const isSuccess = normalizedStatus === 'success' || normalizedStatus === 'completed';
  const isFailed = normalizedStatus === 'failed';
  const StatusIcon = isSuccess ? FaCheckCircle : isFailed ? FaExclamationTriangle : FaClock;

  const handleDownload = async (purchase) => {
    if (!purchase.photo_id || normalizeStatus(purchase.payment_status) !== 'completed') return;

    setDownloadingId(purchase.id);
    try {
      const blob = await downloadPhoto(purchase.photo_id);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.download = getPhotoDisplayName(purchase);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Le telechargement a echoue.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="checkout-page">
        <div className="checkout-loading">
          <div className="spinner"></div>
          <p>Verification du paiement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <section className={`checkout-result-header ${isSuccess ? 'success' : isFailed ? 'failed' : 'pending'}`}>
        <StatusIcon />
        <div>
          <p className="checkout-kicker">Retour CinetPay</p>
          <h1>{isSuccess ? 'Paiement confirme' : isFailed ? 'Paiement echoue' : 'Paiement en verification'}</h1>
          <p>
            {isSuccess
              ? 'Vos photos achetees sont disponibles ci-dessous.'
              : isFailed
                ? 'Le paiement n a pas ete finalise. Vous pouvez relancer le paiement depuis votre panier ou vos evenements.'
                : 'La confirmation peut prendre quelques instants. Actualisez cette page si necessaire.'}
          </p>
          {transaction && <code>{transaction}</code>}
        </div>
      </section>

      {error && <div className="error-message checkout-error">{error}</div>}

      {displayPurchases.length === 0 ? (
        <section className="checkout-empty checkout-result-empty">
          <FaImages />
          <p>Aucune photo trouvee pour cette transaction.</p>
          <Link to="/payment-history">Voir mon historique</Link>
        </section>
      ) : (
        <section className="checkout-purchased-grid">
          {displayPurchases.map(purchase => {
            const purchaseStatus = normalizeStatus(purchase.payment_status);
            const photo = purchase.photo || {};
            const imageUrl = getImageUrl(photo.filename || photo.url);

            return (
              <article key={purchase.id} className="checkout-purchased-card">
                {imageUrl ? (
                  <img src={imageUrl} alt={getPhotoDisplayName(purchase)} />
                ) : (
                  <div className="checkout-photo-placeholder"><FaImages /></div>
                )}
                <div>
                  <strong>{getPhotoDisplayName(purchase)}</strong>
                  <span>{photo.event?.name || 'Evenement'}</span>
                  <span>{Number(purchase.amount || 0).toFixed(2)} F</span>
                </div>
                <button
                  type="button"
                  disabled={purchaseStatus !== 'completed' || downloadingId === purchase.id}
                  onClick={() => handleDownload(purchase)}
                >
                  <FaDownload />
                  <span>{downloadingId === purchase.id ? 'Telechargement...' : 'Telecharger'}</span>
                </button>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

export default CheckoutResult;
